import { FeedbackStatus, IFeedback } from '@/models/feedback.model';
import { getStatusTypeOptions } from '../helpers/getOptions';
import { Link, Select, TextTruncate, Tooltip } from '@/ui';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'react-i18next';
import { ROUTES } from '@/models/router.model';
import { formatDate } from '@/utils';
import { DateFormats } from '@/constants/dates';
import { TrashIcon } from '@/icons';
import { COLORS } from '@/theme';
import { StyledActionButton } from '../components/Styled';

type TGetTableColumns = {
  t: TFunction;
  onChangeStatus?: (data: { id: string; status: FeedbackStatus }) => void;
  onDelete?: (feedback: IFeedback) => void;
  // When false, the status Select is rendered read-only (view-only roles must not
  // see a write control — the backend rejects the change with 403 anyway).
  canManageStatus?: boolean;
};

export const getDeleteColumn = (onDelete?: (feedback: IFeedback) => void): ColumnsType<IFeedback> =>
  onDelete
    ? [
        {
          title: '',
          key: 'delete',
          render: (_: unknown, record: IFeedback) => (
            <StyledActionButton $color={COLORS.RED} onClick={() => onDelete(record)}>
              <TrashIcon />
            </StyledActionButton>
          ),
        },
      ]
    : [];

export const getManagerColumn = (title: string): ColumnsType<IFeedback>[number] => ({
  title,
  dataIndex: 'manager',
  key: 'manager',
  render: (_: unknown, record: IFeedback) => record.manager?.full_name ?? '—',
});

export const getContactsFeedbacksTableColumns = ({
  t,
  onChangeStatus,
  onDelete,
  canManageStatus,
}: TGetTableColumns): ColumnsType<IFeedback> => [
  {
    title: t('label_name'),
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: t('label_phone'),
    dataIndex: 'phone',
    key: 'phone',
  },
  {
    title: t('label_comment'),
    dataIndex: 'comment',
    key: 'comment',
    width: 250,
    render: (comment) => (
      <div>
        <Tooltip title={comment}>
          <TextTruncate text={comment} rows={1} style={{ width: 250 }} />
        </Tooltip>
      </div>
    ),
  },
  {
    title: t('label_date'),
    dataIndex: 'created_at',
    key: 'created_at',
    render: (created_at) => formatDate(created_at, DateFormats.EUROPEAN),
  },
  getManagerColumn(t('label_manager')),
  {
    title: t('label_status'),
    dataIndex: 'status',
    key: 'status',
    render: (_, record) => {
      const handleSelect = (value: FeedbackStatus) => {
        onChangeStatus?.({
          id: record.id,
          status: value,
        });
      };
      return (
        <Select
          value={record.status}
          onChange={handleSelect}
          disabled={!canManageStatus}
          options={getStatusTypeOptions(t)}
        />
      );
    },
  },
  ...getDeleteColumn(onDelete),
];

export const getProductAvailabilityTableColumns = ({
  t,
  onChangeStatus,
  onDelete,
  canManageStatus,
}: TGetTableColumns): ColumnsType<IFeedback> => [
  {
    title: t('label_product_name'),
    dataIndex: 'name',
    key: 'model',
    render: (_, record) => (
      <Link path={ROUTES.PRODUCT_DETAILS.replace(':id', record.model?.id ?? '')}>{record?.model?.name ?? '-'}</Link>
    ),
  },
  {
    title: t('label_name'),
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: t('label_phone'),
    dataIndex: 'phone',
    key: 'phone',
    width: 200,
  },
  {
    title: t('label_email'),
    dataIndex: 'email',
    key: 'email',
  },
  getManagerColumn(t('label_manager')),
  {
    title: t('label_status'),
    dataIndex: 'status',
    key: 'status',
    render: (_, record) => {
      const handleSelect = (value: FeedbackStatus) => {
        onChangeStatus?.({
          id: record.id,
          status: value,
        });
      };
      return (
        <Select
          value={record.status}
          onChange={handleSelect}
          disabled={!canManageStatus}
          options={getStatusTypeOptions(t)}
        />
      );
    },
  },
  ...getDeleteColumn(onDelete),
];

export const getOrderQuestionsTableColumns = ({
  t,
  onChangeStatus,
  onDelete,
  canManageStatus,
}: TGetTableColumns): ColumnsType<IFeedback> => [
  {
    title: t('label_order_number'),
    dataIndex: 'type',
    key: 'type',
    width: 150,
    render: (_, record) => (
      <Link path={ROUTES.ORDER_DETAILS.replace(':id', record.model?.id ?? '')}>{record?.model?.number ?? '-'}</Link>
    ),
  },
  {
    title: t('label_name'),
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: t('label_phone'),
    dataIndex: 'phone',
    key: 'phone',
    width: 200,
  },
  {
    title: t('label_reason'),
    dataIndex: 'request_type',
    key: 'request_type',
    render: (request_type) => t(request_type),
    width: 250,
  },
  {
    title: t('label_comment'),
    dataIndex: 'comment',
    key: 'comment',
    width: 300,
    ellipsis: true,
    render: (comment) => (
      <Tooltip title={comment}>
        <TextTruncate text={comment} rows={1} style={{ width: 250 }} />
      </Tooltip>
    ),
  },
  getManagerColumn(t('label_manager')),
  {
    title: t('label_status'),
    dataIndex: 'status',
    key: 'status',
    render: (_, record) => {
      const handleSelect = (value: FeedbackStatus) => {
        onChangeStatus?.({
          id: record.id,
          status: value,
        });
      };
      return (
        <Select
          value={record.status}
          onChange={handleSelect}
          disabled={!canManageStatus}
          options={getStatusTypeOptions(t)}
        />
      );
    },
  },
  ...getDeleteColumn(onDelete),
];

export const getPartnersTableColumns = ({
  t,
  onChangeStatus,
  onDelete,
  canManageStatus,
}: TGetTableColumns): ColumnsType<IFeedback> => [
  {
    title: t('label_shop'),
    dataIndex: 'model',
    key: 'type',
    render: (_, record) => <span>{record?.model?.title ?? '-'}</span>,
  },
  {
    title: t('label_phone'),
    dataIndex: 'phone',
    key: 'phone',
  },
  {
    title: t('label_email'),
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: t('label_comment'),
    dataIndex: 'comment',
    key: 'comment',
    render: (comment) => (
      <div>
        <Tooltip title={comment}>
          <TextTruncate text={comment} rows={1} style={{ width: 250 }} />
        </Tooltip>
      </div>
    ),
  },
  getManagerColumn(t('label_manager')),
  {
    title: t('label_status'),
    dataIndex: 'status',
    key: 'status',
    render: (_, record) => {
      const handleSelect = (value: FeedbackStatus) => {
        onChangeStatus?.({
          id: record.id,
          status: value,
        });
      };
      return (
        <Select
          value={record.status}
          onChange={handleSelect}
          disabled={!canManageStatus}
          options={getStatusTypeOptions(t)}
        />
      );
    },
  },
  ...getDeleteColumn(onDelete),
];
